import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { LOGO_URL as BRAND_LOGO, ColorBar, RegMark } from "@/components/brand";

const LOGO_URL = BRAND_LOGO;

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center" role="status">
        <Loader2 className="animate-spin text-ink" size={32} aria-label="Loading" />
      </div>
    );
  }

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join(" "));
      else setError("Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-paper flex flex-col">
      <ColorBar />
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex relative flex-col justify-between bg-ink text-paper p-12 xl:p-16 overflow-hidden">
          <span className="self-start bg-paper px-3 py-2 rounded-[3px]"><img src={LOGO_URL} alt="Print Xpress" className="h-8 w-auto" /></span>
          <div>
            <p className="font-display font-bold text-[clamp(3rem,6vw,5.5rem)] leading-[0.92] tracking-[-0.045em]">
              Leads in.<br />Jobs out.
            </p>
            <p className="mt-6 text-paper/70 text-lg max-w-sm leading-relaxed">
              Quotes, orders, staff and the gifting catalogue for all three branches.
            </p>
          </div>
          <div className="flex items-center gap-3 text-paper/40 text-sm">
            <RegMark size={18} /> Print Xpress admin
          </div>
        </div>

        <div className="flex items-center justify-center px-5 py-16 sm:px-12">
          <div className="w-full max-w-sm">
            <span className="lg:hidden inline-block mb-10"><img src={LOGO_URL} alt="Print Xpress" className="h-9 w-auto" /></span>
            <h1 className="font-display font-bold text-4xl tracking-[-0.03em] text-ink">Sign in</h1>
            <p className="mt-2 text-graphite">Use the email and password your admin gave you.</p>

            <form onSubmit={handleSubmit} data-testid="login-form" className="mt-10 space-y-5">
              <div>
                <label htmlFor="login-email" className="field-label">Email</label>
                <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  autoComplete="username" data-testid="login-email-input" className="field" />
              </div>
              <div>
                <label htmlFor="login-password" className="field-label">Password</label>
                <input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  autoComplete="current-password" data-testid="login-password-input" className="field" />
              </div>
              {error && (
                <p data-testid="login-error" role="alert" className="text-sm text-[#B00020] border-l-2 border-[#B00020] pl-3">{error}</p>
              )}
              <button type="submit" disabled={submitting} data-testid="login-submit-button" className="btn btn-ink w-full">
                {submitting && <Loader2 className="animate-spin" size={16} aria-hidden="true" />}
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <a href="/" data-testid="back-to-home" className="mt-10 inline-block text-sm text-graphite ink-link hover:text-ink">
              Back to printxpress.shop
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
